import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import { useSocket } from '../../../contexts/SocketContext';
import { useSpec } from '../../../contexts/SpecContext';
import type { UrlCheckStatus } from './useUrlCheckSocket';

export const useSpecificationsData = () => {
  const { activeSpec, selectSpec, clearSpec } = useSpec();
  const { addEventListener } = useSocket();
  const [schemas, setSchemas] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteSchemaId, setDeleteSchemaId] = useState<string | null>(null);
  const [postmanEnvStatus, setPostmanEnvStatus] = useState<Record<string, boolean>>({});
  const [urlCheckStatus, setUrlCheckStatus] = useState<Record<string, UrlCheckStatus>>({});

  const loadSchemas = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/schemas`);
      if (response.ok) {
        const data = await response.json();
        const loadedSchemas = data.schemas || [];
        setSchemas(loadedSchemas);

        // URL check status from DB (single source of truth)
        const urlStatusMap: Record<string, UrlCheckStatus> = {};
        for (const s of loadedSchemas) {
          urlStatusMap[s.id] = (s.urlCheckStatus as UrlCheckStatus) || 'idle';
        }
        setUrlCheckStatus(urlStatusMap);

        // Fetch env status for postman schemas (requires checking files on disk)
        const postmanSchemas = loadedSchemas.filter((s: any) => s.specCategory === 'postman');
        if (postmanSchemas.length > 0) {
          const statusResults = await Promise.all(
            postmanSchemas.map(async (s: any) => {
              try {
                const res = await fetch(`${API_BASE_URL}/api/postman/check/${s.id}`);
                if (res.ok) {
                  const checkData = await res.json();
                  return { id: s.id, hasEnv: checkData.hasEnvironment };
                }
              } catch {
                /* ignore */
              }
              return { id: s.id, hasEnv: false };
            }),
          );

          const statusMap: Record<string, boolean> = {};
          for (const r of statusResults) {
            statusMap[r.id] = r.hasEnv;
          }
          setPostmanEnvStatus(statusMap);
        }
      }
    } catch (error) {
      console.error('Failed to load schemas:', error);
    }
  }, []);

  useEffect(() => {
    loadSchemas();
  }, [loadSchemas]);

  // Listen for url-check-complete to update card grid badges
  // (url-check-progress is only needed by the detail page's useUrlCheckSocket)
  useEffect(() => {
    const cleanup = addEventListener('url-check-complete', (data: { schemaId: string; allAvailable: boolean }) => {
      setUrlCheckStatus((prev) => ({
        ...prev,
        [data.schemaId]: data.allAvailable ? 'available' : 'unavailable',
      }));
    });

    return cleanup;
  }, [addEventListener]);

  const handleFiles = useCallback(
    async (files: FileList | File[]): Promise<void> => {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(
        (file) =>
          file.type === 'application/json' ||
          file.name.endsWith('.json') ||
          file.name.endsWith('.yaml') ||
          file.name.endsWith('.yml'),
      );

      if (validFiles.length === 0) {
        toast.error('Please upload JSON or YAML files only');
        return;
      }

      setLoading(true);

      for (const file of validFiles) {
        try {
          const formData = new FormData();
          formData.append('schemaFile', file);

          const response = await fetch(`${API_BASE_URL}/api/schemas/upload`, {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            toast.success(`${file.name} uploaded successfully`);
            loadSchemas();
          } else {
            const errorData = await response.json();
            toast.error(`Failed to upload ${file.name}: ${errorData.error}`);
          }
        } catch (error: any) {
          toast.error(`Upload failed: ${error.message}. Schema will not persist after page refresh.`);

          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = JSON.parse(e.target?.result as string);
              const newSchema = {
                id: Date.now().toString(),
                name: content.info?.title || file.name.replace(/\.(json|yaml|yml)$/, ''),
                version: content.info?.version || '1.0.0',
                type: content.openapi ? `OpenAPI ${content.openapi}` : 'OpenAPI 3.0',
                size: `${Math.round(file.size / 1024)} KB`,
                endpointCount: Object.keys(content.paths || {}).length,
                lastModified: new Date().toISOString(),
                description: content.info?.description || 'Uploaded schema',
                content: JSON.stringify(content, null, 2),
              };

              setSchemas((prev) => [...prev, newSchema]);
              toast.warning(`${file.name} temporarily stored (won't persist after refresh)`);
            } catch (_parseError) {
              toast.error(`Failed to parse ${file.name}: Invalid JSON format`);
            }
          };
          reader.readAsText(file);
        }
      }

      setLoading(false);
    },
    [loadSchemas],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const renameSchema = useCallback(async (schemaId: string, newName: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/schemas/${schemaId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to rename schema');
    }

    setSchemas((prev) => prev.map((s) => (s.id === schemaId ? { ...s, name: newName } : s)));
    toast.success('Schema renamed');
  }, []);

  const openDeleteModal = (schemaId: string): void => {
    setDeleteSchemaId(schemaId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = (): void => {
    setShowDeleteModal(false);
    setDeleteSchemaId(null);
  };

  const confirmDeleteSchema = async (): Promise<void> => {
    if (!deleteSchemaId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/schemas/${deleteSchemaId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Schema deleted successfully');
        setSchemas(schemas.filter((s) => s.id !== deleteSchemaId));
        if (activeSpec?.id === deleteSchemaId) {
          clearSpec();
        }
      } else {
        toast.error('Failed to delete schema');
      }
    } catch (_error) {
      setSchemas(schemas.filter((s) => s.id !== deleteSchemaId));
      if (activeSpec?.id === deleteSchemaId) {
        clearSpec();
      }
      toast.success('Schema deleted successfully');
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  return {
    activeSpec,
    selectSpec,
    schemas,
    loading,
    showDeleteModal,
    postmanEnvStatus,
    urlCheckStatus,
    handleFileSelect,
    renameSchema,
    openDeleteModal,
    closeDeleteModal,
    confirmDeleteSchema,
  };
};
